import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import ExternalLink from 'components/Icons/ExternalLink'
import Modal from 'components/Modal'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.multi_use_rewards_card'
)

interface HowRoundsWorkModalProps {
  show: boolean
  onClose: () => void
}

const ROUNDS = [
  { round: 1, statuses: ['used', 'used', 'expired'] },
  { round: 2, statuses: ['used', 'used', 'used'] },
  { round: 3, statuses: ['used', 'expired', 'expired'] },
]

type RewardStatus = 'used' | 'expired'

const RewardBox: React.FC<{ status: RewardStatus; index: number }> = ({
  status,
  index,
}) => {
  const [css] = useStyletron()
  const isUsed = status === 'used'

  return (
    <div className="text-center">
      <div
        className={css({
          width: '64px',
          height: '40px',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(isUsed
            ? {
                backgroundColor: '#E9F6EF',
                border: '1px solid #27AE60',
              }
            : {
                backgroundColor: '#FFFFFF',
                border: '1px dashed #CCCCCC',
              }),
        })}
      >
        <Text.BodyText4 bold color={isUsed ? 'grey70' : 'grey40'}>
          R{index + 1}
        </Text.BodyText4>
      </div>
      <Text.BodyText5 color={isUsed ? 'grey70' : 'grey40'} className="mt-xxs">
        {isUsed ? t('how_rounds_work_used') : t('how_rounds_work_expired')}
      </Text.BodyText5>
    </div>
  )
}

const Connector: React.FC<{ status: RewardStatus }> = ({ status }) => {
  const [css] = useStyletron()

  return (
    <div
      className={css({
        width: '24px',
        marginTop: '19px',
        borderTop:
          status === 'expired' ? '2px dashed #CCCCCC' : '2px dashed #80D4B4',
        flexShrink: 0,
      })}
    />
  )
}

const RoundSection: React.FC<{
  round: number
  statuses: string[]
}> = ({ round, statuses }) => {
  const [css] = useStyletron()

  return (
    <div style={{ padding: '16px' }}>
      <div
        className={css({
          marginBottom: '12px',
          fontFamily: 'Lato, sans-serif',
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: '12px',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: '#999999',
        })}
      >
        {t('how_rounds_work_round', { number: round })}
      </div>
      <div className="d-flex align-items-start" style={{ gap: '8px' }}>
        {statuses.map((status, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <Connector status={status as RewardStatus} />}
            <RewardBox status={status as RewardStatus} index={idx} />
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

const HowRoundsWorkModal: React.FC<HowRoundsWorkModalProps> = ({
  show,
  onClose,
}) => {
  const [css] = useStyletron()

  return (
    <Modal show={show} onModalClose={onClose}>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '1px solid #E0E0E0',
          position: 'relative',
          zIndex: 2,
          backgroundColor: '#FFFFFF',
        })}
      >
        <Text.Header4
          className={css({ margin: 0, padding: 0, lineHeight: '1' })}
        >
          {t('how_rounds_work_title')}
        </Text.Header4>
        <span
          className={css({
            fontSize: '24px',
            color: '#666666',
            cursor: 'pointer',
            lineHeight: '1',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
          onClick={onClose}
          role="button"
          tabIndex={0}
          aria-label="Close"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClose()
            }
          }}
        >
          &times;
        </span>
      </div>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '12px' }}>
          <Text.BodyText3 bold>{t('how_rounds_work_intro')}</Text.BodyText3>
        </div>
        <ul className="mb-m pl-m">
          <li className="mb-xxs">
            <Text.BodyText4 color="grey70">
              {t('how_rounds_work_bullet_1')}
            </Text.BodyText4>
          </li>
          <li className="mb-xxs">
            <Text.BodyText4 color="grey70">
              {t('how_rounds_work_bullet_2')}
            </Text.BodyText4>
          </li>
          <li className="mb-xxs">
            <Text.BodyText4 color="grey70">
              {t('how_rounds_work_bullet_3')}
            </Text.BodyText4>
          </li>
          <li className="mb-xxs">
            <Text.BodyText4 color="grey70">
              {t('how_rounds_work_bullet_4')}
            </Text.BodyText4>
          </li>
          <li>
            <Text.BodyText4 color="grey70">
              {t('how_rounds_work_bullet_5')}
            </Text.BodyText4>
          </li>
        </ul>

        <div
          className={css({
            backgroundColor: '#F8F8F8',
            borderRadius: '8px',
            border: '1px solid #E0E0E0',
            marginBottom: '16px',
          })}
        >
          {ROUNDS.map(({ round, statuses }, roundIdx) => (
            <div key={round}>
              {roundIdx > 0 && (
                <div
                  style={{ borderTop: '1px solid #E0E0E0', margin: '0 16px' }}
                />
              )}
              <RoundSection round={round} statuses={statuses} />
            </div>
          ))}
        </div>

        <a
          href="https://help.thanx.com/hc/en-us/articles/34781899943319-Configuring-rewards-for-multiple-redemptions-Multi-Use-Rewards"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text.BodyText4 color="primary">
            {t('how_rounds_work_learn_more')}{' '}
            <ExternalLink className="ml-xxs" />
          </Text.BodyText4>
        </a>
      </div>
    </Modal>
  )
}

export default HowRoundsWorkModal
